/**
 * `PlatformApiClient` — única porta de I/O da Plataforma (D-10.4, Task 10).
 *
 * Invariantes:
 * - **O browser nunca fala direto com o Supabase/Postgres** (invariante 4 do
 *   ARCHITECTURE-MAP). Em dev a base é o `apps/api-dev`; em produção é o
 *   Cloudflare Worker, que encaminha para as Edge Functions.
 * - **Toda resposta passa por validação de borda** (`assert*` de `api/types.ts`
 *   e de `packages/ui`): se o servidor mudar o contrato, a tela falha com erro
 *   explícito em vez de renderizar `undefined`.
 * - **`fetch` é injetável** (`fetchImpl`) para teste sem navegador e para o
 *   E2E apontar ao servidor efêmero do `createApiServer()`.
 * - **Fonte inexistente não é erro nem zero**: rota ainda não publicada (ex.:
 *   RMA, Task 15) devolve `null` e o dashboard explica a ausência. O mesmo vale
 *   para rota do contrato que ainda não existe no caminho de produção
 *   (`Worker`): ela entra como **fonte aguardando contrato**, não como erro da
 *   tela inteira nem como zero (D-10.26).
 */
import {
  assertBlingCatalogSnapshot,
  assertBlingReprocessResult,
  assertBlingSyncLogEntry,
  assertCrmCompany,
  assertCrmCustomer,
  assertCrmOpportunity,
  assertCrmQuote,
  assertCrmSegment,
  assertCrmTask,
  assertPlatformAuditEvent,
  assertPlatformCatalogEntry,
  assertPlatformCatalogProduct,
  assertPlatformConversation,
  assertQuickReply,
  assertSavedFlow,
  assertWhatsAppGroup,
  assertPlatformNotification,
  assertPlatformOrder,
  assertFulfillmentTask,
  assertPlatformRmaCase,
  assertPlatformUnifiedOrder,
  assertPlatformIntegrationStatus,
  assertPricingSummaryResponse,
  assertFinancialSummaryResponse,
  assertMarketingFeatureFlag,
  assertPresaleHighlight,
  assertBanner,
  assertCoupon,
  type Banner,
  type Coupon,
  type CouponDiscountType,
  type BlingCatalogSnapshot,
  type BlingReprocessResult,
  type BlingSyncLogEntry,
  type CrmCompany,
  type CrmCustomer,
  type CrmOpportunity,
  type CrmQuote,
  type CrmSegment,
  type CrmTask,
  type FulfillmentTask,
  type PlatformAuditEvent,
  type PlatformCatalogEntry,
  type PlatformCatalogProduct,
  type PlatformConversation,
  type QuickReply,
  type QuickReplyInput,
  type SavedFlow,
  type SavedFlowInput,
  type WhatsAppGroup,
  type PlatformMetrics,
  type PlatformNotification,
  type PlatformOrder,
  type PlatformRmaCase,
  type PlatformUnifiedOrder,
  type OrdersSyncResult,
  type OrdersSyncState,
  type PlatformIntegrationStatus,
  type PricingSummaryResponse,
  type FinancialSummaryResponse,
  type MarketingFeatureFlag,
  type MarketingFeatureKey,
  type PresaleHighlight
} from "./types.js";
import {
  assertFinanceSettings,
  assertPayable,
  assertReceivable,
  type FinanceAction,
  type FinanceSettings,
  type NewPayableInput,
  type NewReceivableInput,
  type Payable,
  type Receivable,
  type SettleInput
} from "../finance/types.js";
import type { ChatOperators } from "./ai-types.js";
import type { InboxStats } from "../../../../packages/integration-wapi/src/stats.js";
import type { AiCatalogIndexResult, AiDraft, AiEnrichment, AiEnrichmentDraft, AiEnrichmentTarget, AiMemoryItem, AiOverview, AiSecretStatus, AiSettings, AiStatus, AiTestResult } from "./ai-types.js";
import { assertBranchStockSnapshot, type BranchStockSnapshot } from "../catalog/branch-stock.js";
import {
  assertMarketplaceListing,
  assertPaymentLink,
  assertProductApplication,
  type MarketplaceListing,
  type PaymentLink,
  type ProductApplication
} from "../contracts/platform-extras.js";
import { emptyPricingSummary, type DashboardDataset } from "../dashboard/kpis.js";
import { emptyFinancialDataset, type FinancialDataset } from "../financial/dataset.js";
import {
  assertStaffUser,
  assertStaffUserEvent,
  type NewStaffUserInput,
  type StaffUser,
  type StaffUserEvent,
  type StaffUserPatch
} from "../users/model.js";
import type { CatalogDataset } from "../catalog/dataset.js";
import type { OrdersDataset } from "../orders/dataset.js";
import type { SalesOriginOption } from "../orders/acquisition.js";
import type { OrdersRange } from "../period.js";
import type { OrderTransitionInput, PlatformOrderOrigin, PlatformOrderState } from "../orders/state-machine.js";
import type { FulfillmentDataset } from "../fulfillment/dataset.js";
import type { FulfillmentStage, FulfillmentStageInput, OccurrenceKind } from "../fulfillment/state-machine.js";
import type { RmaDataset } from "../rma/dataset.js";
import type { RmaState, RmaTransitionInput } from "../rma/state-machine.js";
import type { CrmDataset } from "../crm/dataset.js";
import { assertCrm360Payload, type Crm360Payload } from "../crm/contract.js";
import type { CrmTaskKind, LeadStage, OpportunitySource, StageTransitionInput } from "../crm/pipeline.js";
import type { OrderCartItem, OrderCustomer } from "../../../../packages/checkout/src/index.js";

export interface HttpResponseLike {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

export type FetchLike = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string }
) => Promise<HttpResponseLike>;

export interface PlatformApiClientOptions {
  /** Base da API (sem barra final). Vazio = mesma origem do SPA. */
  baseUrl?: string;
  fetchImpl?: FetchLike;
  /** Token de staff da sessão; vira `Authorization: Bearer` quando presente. */
  getAccessToken?: () => string | null;
}

/**
 * Patch da camada comercial (Task 11). Apenas campos cuja autoridade é a
 * Plataforma (migration 0019): o tipo é a primeira barreira contra uma tela
 * tentar escrever campo de referência do Bling.
 *
 * **Autoria não entra aqui.** Quem editou é derivado da sessão autenticada no
 * servidor (`catalog_change_history.actor_profile_id`, migration 0030); se o
 * cliente pudesse declarar o autor, o histórico auditável seria forjável — e o
 * servidor recusa a tentativa com 422 em vez de ignorá-la.
 */
export interface CommercialLayerPatch {
  commercialName?: string;
  shortDescription?: string;
  longDescription?: string;
  seoTitle?: string;
  seoDescription?: string;
  /** À venda na Loja. `false` tira o produto da vitrine; Bling segue dono de preço e estoque. */
  published?: boolean;
}

/**
 * Mídia do produto (Plano Loja/Twotrees, Fase 2) — mesma autoridade da
 * Plataforma da camada comercial acima. `kind` cobre imagem/vídeo/manual/PDF/
 * documento e `spin_frame` (ângulo fixo para o viewer 360° do Hero da Loja,
 * Fase 3). Autoria também não entra aqui, pelo mesmo motivo.
 */
export interface MediaCreatePatch {
  kind: "image" | "video" | "manual" | "pdf" | "document" | "spin_frame";
  url: string;
  title?: string;
  position?: number;
}

export type MediaUpdatePatch = Partial<MediaCreatePatch>;

/**
 * Registro de uma venda que não nasceu no checkout da Loja (Task 12).
 *
 * `origin` + `externalId` são obrigatórios: é o requisito do brief 3.3
 * ("origem e identificadores externos preservados"). `shippingCents` também é
 * obrigatório — inclusive `0` — porque a Plataforma **não inventa frete**: o
 * operador informa o que foi combinado no canal, e omitir o campo é recusado
 * pelo servidor em vez de virar um total errado.
 *
 * Preço dos itens vem do que foi acordado no canal; o servidor soma o subtotal
 * a partir dos itens e nunca reprecifica pela oferta vigente (mesma regra de
 * `buildOrderFromCart` em `packages/checkout`).
 */
export interface CreatePlatformOrderInput {
  origin: PlatformOrderOrigin;
  externalId: string;
  conversationId?: string;
  customerId?: string;
  customer: OrderCustomer;
  items: OrderCartItem[];
  shippingCents: number;
  /** Origem de aquisição (0062): slug de uma origem cadastrada e ativa. */
  acquisitionOrigin?: string;
  acquisitionStrategy?: string;
  responsibleName?: string;
}

/** Marcação de origem de um pedido que já existe (ex.: importado do Bling). */
export interface OrderAcquisitionInput {
  origin: string;
  strategy?: string;
  responsibleName?: string;
}

/**
 * Nova oportunidade no funil (Task 13).
 *
 * `customerId` é o cliente CANÔNICO (0020): a Plataforma não cria cadastro ao
 * abrir uma negociação, e o servidor recusa id que não existe — senão o funil
 * acumularia oportunidades que não navegam até cliente nenhum.
 *
 * `ownerProfileId` fica FORA do tipo de propósito: o responsável de uma
 * oportunidade nova é quem a está criando, derivado da sessão no servidor
 * (mesma regra de autoria da Task 11/12). Transferir a oportunidade para outra
 * pessoa é outra operação, não um campo de criação.
 */
export interface CreateCrmOpportunityInput {
  customerId: string;
  title: string;
  source: OpportunitySource;
  valueCents?: number;
  conversationId?: string;
  companyId?: string;
}

/**
 * Tarefa/follow-up comercial. `dueAt` e o dono são obrigatórios na 0032 — o
 * dono vem da sessão, então aqui só viaja o prazo.
 */
export interface CreateCrmTaskInput {
  customerId: string;
  opportunityId?: string;
  kind: CrmTaskKind;
  title: string;
  notes?: string;
  dueAt: string;
}

/**
 * Orçamento (proposta). O preço de cada item viaja no corpo porque é o preço
 * ACORDADO na proposta, travado na emissão; o servidor soma os totais e nunca
 * reprecifica pela oferta vigente (mesma regra de `buildOrderFromCart`).
 *
 * `shippingCents` é opcional e `null`-ável: proposta sem frete cotado fica com
 * total aberto e diz isso, em vez de somar um frete inventado.
 */
export interface CreateCrmQuoteInput {
  customerId: string;
  opportunityId?: string;
  items: Array<{ sku: string; description?: string; quantity: number; unitPriceCents: number; productId?: string }>;
  discountCents?: number;
  shippingCents?: number | null;
  validUntil?: string;
}

export class PlatformApiError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "PlatformApiError";
  }
}

/**
 * Base da API por ambiente. `VITE_PLATFORM_API_BASE` aponta para o
 * `apps/api-dev` em desenvolvimento e para o Worker em produção; sem a
 * variável usamos a mesma origem (o dev server do Vite faz proxy de `/api`).
 */
export function resolvePlatformApiBase(env?: { VITE_PLATFORM_API_BASE?: string }): string {
  const configured = env?.VITE_PLATFORM_API_BASE?.trim();
  if (!configured) return "";
  return configured.replace(/\/$/, "");
}

/**
 * Rotas do contrato HTTP que a Plataforma consome. Um mapa único — e não
 * strings espalhadas — porque é aqui que se lê, de uma olhada, **o que já
 * existe em produção e o que ainda não**.
 *
 * `apps/api-dev` (dev/E2E) serve todas. O Cloudflare Worker (produção) ainda
 * NÃO publica `conversations` e `pricingSummary`: a paridade Worker → Edge
 * Functions dessas duas rotas é trabalho de outra frente (Task 18 / contrato
 * `/api/*`). Por isso o cliente trata a ausência delas como **fonte aguardando
 * contrato** (ver `PLATFORM_SOURCE_PENDING_ROUTES`), em vez de derrubar o
 * dashboard inteiro — e os KPIs que dependem delas declaram a ausência.
 */
export const PLATFORM_ROUTES = {
  orders: "/api/checkout/orders",
  conversations: "/api/channels/conversations",
  catalog: "/api/catalog/products",
  // Task 11: catálogo em duas camadas (operacional + comercial + B2B +
  // reservas + histórico). Paridade de produção fechada na Fase 11 do Plano
  // de Ataque R8 (handoff H-10.A): o Worker proxya para
  // `platform-catalog-runtime` (`GET /products`), que monta as duas camadas a
  // partir de `products`/`offers`/`product_commercial`/`b2b_price_tables`/
  // `stock_reservations` — a reserva de carrinho (`commerce-runtime`) e a sua
  // liberação/consumo (`checkout-runtime`) também passaram a gravar de
  // verdade nesta fase, então "disponibilidade consolidada" deixou de ser só
  // o estoque bruto do operacional. 404/501 aqui deixou de ser "aguardando
  // contrato" e passou a ser erro de verdade.
  platformCatalog: "/api/catalog/platform/products",
  // Snapshot operacional do Bling. Paridade de produção fechada na Fase 3 do
  // Plano de Ataque R8 (handoff H-10.A): o Worker já proxya para
  // `bling-sync-runtime` (`GET /snapshot`) — 404 aqui deixou de ser
  // "aguardando contrato" e passou a ser erro de verdade.
  blingSnapshot: "/api/catalog/bling/snapshot",
  // Eventos de `bling_sync_log`: é o destino do link "evento de sync" de cada
  // divergência. Sem esta rota a tela de sincronização não teria o registro
  // de origem para exibir — e o drill-down seria só uma URL. Paridade de
  // produção fechada na Fase 3 do Plano de Ataque R8 (handoff H-10.A).
  blingSyncLog: "/api/catalog/bling/sync-log",
  // Task 17 (R3): reprocessamento de uma divergência — re-enfileira um sync do
  // Bling (`bling_sync_queue`, 0037). É a ação "reprocessar" da tela da Task 11.
  // Paridade de produção fechada na Fase 3 do Plano de Ataque R8 (handoff H-10.A).
  blingReprocess: "/api/catalog/bling/reprocess",
  pricingSummary: "/api/pricing/summary",
  // Task 12: pedido unificado multicanal (`platform_orders`, migration 0031).
  // GET fechou a paridade de produção na Fase 4 do Plano de Ataque R8
  // (handoff H-10.A): a Loja já projeta pedido pago na fila da Plataforma de
  // verdade em produção. POST (criar venda de WhatsApp/marketplace/manual) e
  // `.../transitions` (avançar estágio) continuam só em `apps/api-dev` — as
  // duas exigem saber QUEM é o operador, e isso depende de uma peça de
  // hardening de auth ainda pendente (ver `apps/plataforma/src/auth/
  // session.ts`); 404/501 nessas duas continua "aguardando contrato".
  platformOrders: "/api/platform/orders",
  // Task 13: CRM 360 (`crm_*`, migration 0032). `crmCustomers`/`crmCompanies`
  // são a LEITURA do cadastro que já existe (`customers`+`customer_identities`
  // da 0020 e `companies`+`company_members` da 0018) — não um cadastro novo.
  // Como as de cima, existem em `apps/api-dev` e entram no Worker junto da
  // paridade Worker → Edge Functions (H-10.A), então 404/501 aqui é
  // "aguardando contrato", nunca "cliente sem relacionamento".
  crmCustomers: "/api/crm/customers",
  crmCompanies: "/api/crm/companies",
  crmOpportunities: "/api/crm/opportunities",
  crmQuotes: "/api/crm/quotes",
  crmTasks: "/api/crm/tasks",
  crmSegments: "/api/crm/segments",
  // Visão 360 do cliente CANÔNICO, publicada pelo runtime persistente
  // (`supabase/functions/crm-runtime`, roteada pelo Worker). É a rota que a
  // tela de 360 usa: o servidor resolve a identidade por
  // `resolve_customer_by_identity` (0020) e devolve os registros das cinco
  // fontes já filtrados por aquele cliente — uma chamada, um cliente, em vez
  // de seis listas globais filtradas no browser.
  crm360: "/api/crm/360",
  // Task 14: expedição (`fulfillment_tasks`, migration 0033). Existe em
  // `apps/api-dev` (dev/E2E); no Worker de produção entra junto da paridade
  // Worker → Edge Functions (handoff H-10.A), então 404/501 aqui é
  // "aguardando contrato" — e a tela de expedição declara isso em vez de
  // mostrar "nenhuma expedição".
  fulfillment: "/api/platform/fulfillment",
  notifications: "/api/admin/notifications",
  metrics: "/api/admin/metrics",
  audit: "/api/admin/audit",
  rmaCases: "/api/rma/cases",
  // Task 16: financeiro gerencial (`financial_*`/`managerial_*`/`*_margins`,
  // migration 0035). Existe em `apps/api-dev` (dev/E2E); no Worker de produção
  // entra junto da paridade Worker → Edge Functions (handoff H-16.B), então
  // 404/501 aqui é "aguardando contrato" — e as telas do Financeiro declaram
  // isso em vez de mostrar "tudo conciliado" / "margem zero".
  financialSummary: "/api/platform/financial/summary",
  // Entrega 0 (R3–R5): configuração parametrizável das integrações. Metadados
  // apenas (estado, nomes de secret faltando, flag) — nunca o valor de um
  // segredo (D-R3R5-02/04). Servida por `apps/api-dev`; a paridade Worker→Edge
  // desta rota NÃO entra nesta entrega (é config de ambiente, não dado de
  // negócio — D-R3R5-04), então 404/501 aqui é "aguardando contrato".
  configIntegrations: "/api/platform/config/integrations",
  // Plano Loja/Twotrees, Fase 7/8 — marketing admin→loja (feature flags +
  // faixa de pré-venda). Mesmo Worker → storefront-marketing-runtime.
  marketingFeatureFlags: "/api/platform/marketing/feature-flags",
  marketingPresaleHighlights: "/api/platform/marketing/presale-highlights",
  marketingBanners: "/api/platform/marketing/banners",
  coupons: "/api/platform/coupons",
  // Redesign admin, Fase 6: saldo por filial (hoje só seed demonstrativo no api-dev).
  branchStock: "/api/platform/stock/branches",
  // Usuários da equipe (0058): CRUD, bloqueio rápido e permissão por módulo (só administrador, exceto /me).
  users: "/api/platform/users",
  financePayables: "/api/platform/finance/payables",
  financeReceivables: "/api/platform/finance/receivables",
  financeSettings: "/api/platform/finance/settings",
  // C1/C3/C4: aplicações, link de pagamento de venda de canal e anúncios do Mercado Livre.
  applications: "/api/platform/catalog/applications",
  marketplaceListings: "/api/platform/marketplace/listings"
} as const;

/**
 * Rotas obrigatórias do dataset que podem não existir no ambiente (hoje: o
 * Worker de produção). 404/501 nelas significa "contrato ainda não publicado
 * aqui" — estado honesto e navegável, nunca zero.
 */
export const PLATFORM_SOURCE_PENDING_ROUTES: readonly string[] = [
  PLATFORM_ROUTES.conversations,
  PLATFORM_ROUTES.pricingSummary,
  PLATFORM_ROUTES.platformCatalog,
  PLATFORM_ROUTES.blingSnapshot,
  PLATFORM_ROUTES.blingSyncLog,
  PLATFORM_ROUTES.platformOrders,
  PLATFORM_ROUTES.crmCustomers,
  PLATFORM_ROUTES.crmCompanies,
  PLATFORM_ROUTES.crmOpportunities,
  PLATFORM_ROUTES.crmQuotes,
  PLATFORM_ROUTES.crmTasks,
  PLATFORM_ROUTES.crmSegments,
  PLATFORM_ROUTES.fulfillment,
  PLATFORM_ROUTES.financialSummary,
  PLATFORM_ROUTES.configIntegrations
];

export class PlatformApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: FetchLike;
  private readonly getAccessToken: () => string | null;

  constructor(options: PlatformApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/$/, "");
    // `globalThis.fetch` existe no browser e no Node 18+; o teste injeta o seu.
    // IMPORTANTE: o `fetch` do browser exige `this === window` na chamada. Guardar
    // a referência e chamá-la solta (this.fetchImpl(...)) dispara "Illegal
    // invocation" no navegador (no Node não, por isso só aparece em runtime).
    // Por isso fazemos bind ao globalThis quando usamos o fetch global.
    const rawGlobalFetch = (globalThis as { fetch?: FetchLike }).fetch;
    const globalFetch = rawGlobalFetch ? (rawGlobalFetch.bind(globalThis) as FetchLike) : undefined;
    const fetchImpl = options.fetchImpl ?? globalFetch;
    if (!fetchImpl) throw new Error("fetch_unavailable: pass options.fetchImpl");
    this.fetchImpl = fetchImpl;
    this.getAccessToken = options.getAccessToken ?? (() => null);
  }

  private async request(path: string): Promise<HttpResponseLike> {
    const token = this.getAccessToken();
    const headers: Record<string, string> = { accept: "application/json" };
    if (token) headers.authorization = `Bearer ${token}`;
    return this.fetchImpl(`${this.baseUrl}${path}`, { method: "GET", headers });
  }

  /**
   * Escrita (Task 11). Única porta de mutação: o browser continua falando só
   * com o contrato HTTP, nunca com o banco. O corpo é serializado aqui para
   * que nenhuma tela monte `fetch` por conta própria.
   */
  private async sendJson(path: string, method: "POST" | "PUT" | "PATCH" | "DELETE", body?: unknown): Promise<unknown> {
    const token = this.getAccessToken();
    const headers: Record<string, string> = { accept: "application/json", "content-type": "application/json" };
    if (token) headers.authorization = `Bearer ${token}`;
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    if (!response.ok) {
      // Mantém a causa do servidor na mensagem: recusa por autoridade de campo
      // (422) tem de chegar legível à tela, não virar "erro desconhecido".
      const detail = await response.json().catch(() => null);
      const reason = detail && typeof detail === "object" && "error" in detail ? String((detail as { error: unknown }).error) : "request_failed";
      throw new PlatformApiError(path, response.status, `${reason}:${path}:${response.status}`);
    }
    return response.json();
  }

  private async getJson(path: string): Promise<unknown> {
    const response = await this.request(path);
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    return response.json();
  }

  private async getList(path: string): Promise<unknown[]> {
    const payload = await this.getJson(path);
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload;
  }

  async listOrders(): Promise<PlatformOrder[]> {
    return (await this.getList(PLATFORM_ROUTES.orders)).map(assertPlatformOrder);
  }

  async listConversations(): Promise<PlatformConversation[]> {
    return (await this.getList(PLATFORM_ROUTES.conversations)).map(assertPlatformConversation);
  }

  /**
   * Conversas tolerando **rota ainda não publicada no ambiente**: 404/501
   * devolve `null` (= "aguardando contrato"), o que deixa o dashboard montar
   * com os demais indicadores em vez de falhar inteiro. Erro de verdade (5xx,
   * payload fora do contrato) continua propagando.
   */
  async loadConversations(): Promise<PlatformConversation[] | null> {
    const response = await this.request(PLATFORM_ROUTES.conversations);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(
        PLATFORM_ROUTES.conversations,
        response.status,
        `request_failed:${PLATFORM_ROUTES.conversations}:${response.status}`
      );
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(PLATFORM_ROUTES.conversations, 200, `expected_array_response:${PLATFORM_ROUTES.conversations}`);
    }
    return payload.map(assertPlatformConversation);
  }

  /**
   * Atendimento ao vivo: uma conversa com o histórico completo (a lista não
   * traz mensagens na inbox persistente) e as ações do atendente. Escrita só
   * pelo contrato HTTP — o browser nunca fala com o banco nem com o WAPI.
   */
  async getChannelConversation(id: string): Promise<PlatformConversation> {
    return assertPlatformConversation(await this.getJson(`/api/channels/${encodeURIComponent(id)}`));
  }

  /** Resposta humana pelo WAPI. Recusa (422) chega legível: número não verificado. */
  async sendChannelMessage(id: string, text: string, quotedMessageId?: string): Promise<{ conversation: PlatformConversation; dispatch: "sent" | "queued" }> {
    // `quotedMessageId`: responder a uma mensagem específica (só grupo; o servidor ignora em conversa 1:1).
    const payload = await this.sendJson(`/api/channels/${encodeURIComponent(id)}/send`, "POST", { text, ...(quotedMessageId ? { quotedMessageId } : {}) }) as {
      conversation: unknown;
      audit?: { dispatch?: string };
    };
    return { conversation: assertPlatformConversation(payload.conversation), dispatch: payload.audit?.dispatch === "queued" ? "queued" : "sent" };
  }

  /**
   * Mídia pelo WAPI da TDZAIN (C6). O arquivo vai em base64 no JSON (limite de 12 MB, validado no
   * servidor). Mesmas recusas do texto: 422 com número não verificado ou mídia inválida.
   */
  async sendChannelMedia(
    id: string,
    media: { kind: "image" | "video" | "audio" | "document" | "sticker"; fileName: string; mimeType: string; dataBase64: string; caption?: string }
  ): Promise<{ conversation: PlatformConversation; dispatch: "sent" | "queued" }> {
    const payload = await this.sendJson(`/api/channels/${encodeURIComponent(id)}/media`, "POST", media) as {
      conversation: unknown;
      audit?: { dispatch?: string };
    };
    return { conversation: assertPlatformConversation(payload.conversation), dispatch: payload.audit?.dispatch === "queued" ? "queued" : "sent" };
  }

  /** Reage a uma mensagem (emoji vazio remove). O WhatsApp só recebe depois de a Edge Function confirmar. */
  async reactToMessage(conversationId: string, messageId: string, emoji: string): Promise<void> {
    await this.sendJson(`/api/channels/${encodeURIComponent(conversationId)}/react`, "POST", { messageId, emoji });
  }

  /** Transcreve um áudio recebido; o servidor guarda o texto na mensagem (pedir de novo não cobra de novo). */
  async transcribeMessage(conversationId: string, messageId: string): Promise<string> {
    const payload = await this.sendJson(`/api/channels/${encodeURIComponent(conversationId)}/transcribe`, "POST", { messageId }) as { transcript?: string };
    return payload.transcript ?? "";
  }

  /** Marca como lida: visto azul no WhatsApp e contador de não lidas zerado. */
  async markConversationRead(conversationId: string): Promise<void> {
    await this.sendJson(`/api/channels/${encodeURIComponent(conversationId)}/read`, "POST", {});
  }

  // ---- Assistente de IA (/api/ai/*) -------------------------------------------------------------------------
  async getAiStatus(): Promise<AiStatus> { return await this.getJson("/api/ai/status") as AiStatus; }
  async getAiOverview(): Promise<AiOverview> { return await this.getJson("/api/ai/overview") as AiOverview; }
  async getAiSettings(): Promise<AiSettings> { return await this.getJson("/api/ai/settings") as AiSettings; }
  async saveAiSettings(patch: Record<string, unknown>): Promise<AiSettings> { return await this.sendJson("/api/ai/settings", "PUT", patch) as AiSettings; }
  async listAiSecrets(): Promise<AiSecretStatus[]> { return (await this.getList("/api/ai/secrets")) as AiSecretStatus[]; }
  async saveAiSecret(name: string, value: string): Promise<AiSecretStatus> { return await this.sendJson("/api/ai/secrets", "PUT", { name, value }) as AiSecretStatus; }
  async removeAiSecret(name: string): Promise<void> { await this.sendJson(`/api/ai/secrets/${encodeURIComponent(name)}`, "DELETE"); }
  // ---- IA do catálogo (OpenRouter): enriquecimento de cadastro e índice da busca da Loja ----------------------
  async enrichProductWithAi(productId: string, target: AiEnrichmentTarget, guidance = ""): Promise<{ id: string; status: "draft"; draft: AiEnrichmentDraft; costUsd: number }> {
    return await this.sendJson("/api/ai/catalog/enrich", "POST", { productId, target, guidance }) as { id: string; status: "draft"; draft: AiEnrichmentDraft; costUsd: number };
  }
  async listProductEnrichments(productId: string): Promise<AiEnrichment[]> { return (await this.getList(`/api/ai/catalog/enrichments?productId=${encodeURIComponent(productId)}`)) as AiEnrichment[]; }
  async resolveProductEnrichment(id: string, action: "approve" | "discard", edited?: Partial<AiEnrichmentDraft>): Promise<{ status: string; applied?: string }> {
    return await this.sendJson(`/api/ai/catalog/enrichments/${encodeURIComponent(id)}/${action}`, "POST", edited ?? {}) as { status: string; applied?: string };
  }
  async reindexCatalogForAiSearch(force = false): Promise<AiCatalogIndexResult> { return await this.sendJson("/api/ai/catalog/index", "POST", { force }) as AiCatalogIndexResult; }
  async testAiSecret(name: string): Promise<{ ok: boolean; detail: string }> { return await this.sendJson("/api/ai/secrets/test", "POST", { name }) as { ok: boolean; detail: string }; }
  async listAiMemory(status?: string): Promise<AiMemoryItem[]> { return (await this.getList(`/api/ai/memory${status ? `?status=${status}` : ""}`)) as AiMemoryItem[]; }
  async curateAiMemory(id: string, patch: { status?: string; answer?: string }): Promise<void> { await this.sendJson(`/api/ai/memory/${encodeURIComponent(id)}`, "PATCH", patch); }
  async testAi(text: string): Promise<AiTestResult> { return await this.sendJson("/api/ai/test", "POST", { text }) as AiTestResult; }
  async listAiDrafts(conversationId: string): Promise<AiDraft[]> { return (await this.getList(`/api/ai/drafts?conversationId=${encodeURIComponent(conversationId)}`)) as AiDraft[]; }
  async resolveAiDraft(id: string, action: "approve" | "discard", text?: string): Promise<void> { await this.sendJson(`/api/ai/drafts/${encodeURIComponent(id)}/${action}`, "POST", text === undefined ? {} : { text }); }

  async listWhatsAppGroups(): Promise<WhatsAppGroup[]> {
    return (await this.getList("/api/channels/groups")).map(assertWhatsAppGroup);
  }

  /**
   * Tags de conversa/grupo (id → tags). Rota ausente (404, ex.: produção ainda sem a tabela)
   * vira `null`: a tela declara "aguardando contrato" em vez de fingir que não há tags.
   */
  async getConversationTags(): Promise<Record<string, string[]> | null> {
    try {
      const payload = await this.getJson("/api/channels/tags");
      if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return null;
      return payload as Record<string, string[]>;
    } catch (error) {
      if (error instanceof PlatformApiError && (error.status === 404 || error.status === 405)) return null;
      throw error;
    }
  }

  async setConversationTags(id: string, tags: string[]): Promise<string[]> {
    const payload = await this.sendJson(`/api/channels/tags/${encodeURIComponent(id)}`, "PUT", { tags }) as { tags: string[] };
    return payload.tags;
  }

  /** Responde a UM grupo (não existe envio em massa). */
  async sendToWhatsAppGroup(groupId: string, text: string): Promise<{ groupId: string; sentAt: string }> {
    const payload = await this.sendJson(`/api/channels/groups/${encodeURIComponent(groupId)}/send`, "POST", { text }) as { groupId: string; sentAt: string };
    return { groupId: payload.groupId, sentAt: payload.sentAt };
  }

  async listFlows(): Promise<SavedFlow[]> {
    return (await this.getList("/api/channels/flows")).map(assertSavedFlow);
  }

  async saveFlow(input: SavedFlowInput, id?: string): Promise<SavedFlow> {
    return assertSavedFlow(await this.sendJson(id ? `/api/channels/flows/${encodeURIComponent(id)}` : "/api/channels/flows", id ? "PUT" : "POST", input));
  }

  async deleteFlow(id: string): Promise<void> {
    await this.sendJson(`/api/channels/flows/${encodeURIComponent(id)}`, "DELETE");
  }

  async listQuickReplies(): Promise<QuickReply[]> {
    return (await this.getList("/api/channels/quick-replies")).map(assertQuickReply);
  }

  async createQuickReply(input: QuickReplyInput): Promise<QuickReply> {
    return assertQuickReply(await this.sendJson("/api/channels/quick-replies", "POST", input));
  }

  async updateQuickReply(id: string, patch: Partial<QuickReplyInput>): Promise<QuickReply> {
    return assertQuickReply(await this.sendJson(`/api/channels/quick-replies/${encodeURIComponent(id)}`, "PATCH", patch));
  }

  async deleteQuickReply(id: string): Promise<void> {
    await this.sendJson(`/api/channels/quick-replies/${encodeURIComponent(id)}`, "DELETE");
  }

  /** `takeover`: o atendente assume (IA para). `resume`: devolve à IA. */
  async setChannelConversationMode(id: string, action: "takeover" | "resume"): Promise<PlatformConversation> {
    return assertPlatformConversation(await this.sendJson(`/api/channels/${encodeURIComponent(id)}/${action}`, "POST"));
  }

  /** Transfere a conversa para um operador (ou a libera com `null`). Atribuir implica atendimento humano. */
  async assignChannelConversation(id: string, assigneeId: string | null): Promise<PlatformConversation> {
    return assertPlatformConversation(await this.sendJson(`/api/channels/${encodeURIComponent(id)}/assign`, "POST", { assigneeId }));
  }

  /** Operadores que podem atender e quem é o usuário logado. */
  async getChannelOperators(): Promise<ChatOperators> {
    const body = await this.getJson("/api/channels/operators") as Partial<ChatOperators> | null;
    return { me: body?.me ?? null, operators: Array.isArray(body?.operators) ? body.operators : [] };
  }

  /** Triagem por IA: quais conversas pedem atenção primeiro (só ordena; nunca responde ao cliente). */
  async triageConversations(items: Array<{ id: string; name: string; waitingMinutes: number; unassigned: boolean }>): Promise<Array<{ id: string; urgency: number; reason: string }>> {
    const body = await this.sendJson("/api/ai/triage", "POST", { items }) as { items?: Array<{ id: string; urgency: number; reason: string }> };
    return Array.isArray(body?.items) ? body.items : [];
  }

  /** Tempo médio de primeira resposta e de atendimento (últimos 30 dias). */
  async getInboxStats(): Promise<InboxStats> {
    return await this.getJson("/api/channels/stats") as InboxStats;
  }

  /** Resumo de margem, com a mesma tolerância de `loadConversations()`. */
  async loadPricingSummary(): Promise<PricingSummaryResponse | null> {
    const response = await this.request(PLATFORM_ROUTES.pricingSummary);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(
        PLATFORM_ROUTES.pricingSummary,
        response.status,
        `request_failed:${PLATFORM_ROUTES.pricingSummary}:${response.status}`
      );
    }
    return assertPricingSummaryResponse(await response.json());
  }

  async listCatalogEntries(): Promise<PlatformCatalogEntry[]> {
    return (await this.getList(PLATFORM_ROUTES.catalog)).map(assertPlatformCatalogEntry);
  }

  /**
   * Catálogo em duas camadas (Task 11). `null` = rota não publicada neste
   * ambiente — as telas de catálogo declaram a ausência e o KPI de estoque
   * crítico volta a calcular sem reservas, dizendo isso na nota.
   */
  async loadPlatformCatalog(): Promise<PlatformCatalogProduct[] | null> {
    const path = PLATFORM_ROUTES.platformCatalog;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload.map(assertPlatformCatalogProduct);
  }

  /**
   * Snapshot operacional do Bling (Task 11). `null` = fonte do operacional
   * ausente; a tela de divergências declara que não há comparação possível em
   * vez de mostrar "tudo sincronizado".
   */
  async loadBlingSnapshot(): Promise<BlingCatalogSnapshot | null> {
    const path = PLATFORM_ROUTES.blingSnapshot;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    return assertBlingCatalogSnapshot(await response.json());
  }

  /**
   * Eventos de sincronização do Bling (Task 11). `null` = rota não publicada
   * neste ambiente: a tela de sincronização declara que o evento de origem não
   * está disponível, em vez de deixar o link da divergência cair no vazio.
   */
  async loadBlingSyncLog(): Promise<BlingSyncLogEntry[] | null> {
    const path = PLATFORM_ROUTES.blingSyncLog;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload.map(assertBlingSyncLogEntry);
  }

  /**
   * Reprocessa uma divergência (Task 17/R3): re-enfileira um sync do Bling para
   * a entidade divergente. A tela de divergências chama isto na ação
   * "reprocessar"; o contrato (api-dev simulador e Edge Function real) devolve o
   * item enfileirado. Erro de borda propaga como `PlatformApiError`.
   */
  async reprocessDivergence(entityType: string, entityId: string): Promise<BlingReprocessResult> {
    const payload = await this.sendJson(PLATFORM_ROUTES.blingReprocess, "POST", { entityType, entityId });
    return assertBlingReprocessResult(payload);
  }

  /**
   * Edição da camada COMERCIAL (Task 11). Só campos de autoridade da
   * Plataforma entram no tipo do patch — o compilador já impede a tela de
   * tentar escrever SKU/estoque/preço-base, e o servidor recusa com 422 se
   * alguém tentar pela rota. A autoria é derivada da sessão no servidor e por
   * isso não viaja no corpo.
   */
  async updateCommercialLayer(productId: string, patch: CommercialLayerPatch): Promise<PlatformCatalogProduct> {
    const path = `${PLATFORM_ROUTES.platformCatalog}/${encodeURIComponent(productId)}/commercial`;
    return assertPlatformCatalogProduct(await this.sendJson(path, "PATCH", patch));
  }

  /** Adiciona uma mídia ao produto (Plano Loja/Twotrees, Fase 2). */
  async addProductMedia(productId: string, patch: MediaCreatePatch): Promise<PlatformCatalogProduct> {
    const path = `${PLATFORM_ROUTES.platformCatalog}/${encodeURIComponent(productId)}/media`;
    return assertPlatformCatalogProduct(await this.sendJson(path, "POST", patch));
  }

  /** Edita uma mídia já cadastrada (reordenar via `position`, trocar título/URL/tipo). */
  async updateProductMedia(productId: string, mediaId: string, patch: MediaUpdatePatch): Promise<PlatformCatalogProduct> {
    const path = `${PLATFORM_ROUTES.platformCatalog}/${encodeURIComponent(productId)}/media/${encodeURIComponent(mediaId)}`;
    return assertPlatformCatalogProduct(await this.sendJson(path, "PATCH", patch));
  }

  /** Remove uma mídia do produto. */
  async removeProductMedia(productId: string, mediaId: string): Promise<PlatformCatalogProduct> {
    const path = `${PLATFORM_ROUTES.platformCatalog}/${encodeURIComponent(productId)}/media/${encodeURIComponent(mediaId)}`;
    return assertPlatformCatalogProduct(await this.sendJson(path, "DELETE"));
  }

  /**
   * Plano Loja/Twotrees, Fase 7/8 — marketing admin→loja. Liga/desliga e
   * CRUD da faixa de pré-venda; a Loja reflete sem redeploy (GET
   * /api/marketing/presale-highlights, rota pública separada).
   */
  async listMarketingFeatureFlags(): Promise<MarketingFeatureFlag[]> {
    return (await this.getList(PLATFORM_ROUTES.marketingFeatureFlags)).map(assertMarketingFeatureFlag);
  }

  async setMarketingFeatureFlag(featureKey: MarketingFeatureKey, enabled: boolean): Promise<MarketingFeatureFlag> {
    const path = `${PLATFORM_ROUTES.marketingFeatureFlags}/${encodeURIComponent(featureKey)}`;
    return assertMarketingFeatureFlag(await this.sendJson(path, "PATCH", { enabled }));
  }

  async listPresaleHighlights(): Promise<PresaleHighlight[]> {
    return (await this.getList(PLATFORM_ROUTES.marketingPresaleHighlights)).map(assertPresaleHighlight);
  }

  async addPresaleHighlight(input: { productId: string; headline: string; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }): Promise<PresaleHighlight> {
    return assertPresaleHighlight(await this.sendJson(PLATFORM_ROUTES.marketingPresaleHighlights, "POST", input));
  }

  async updatePresaleHighlight(id: string, patch: { headline?: string; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }): Promise<PresaleHighlight> {
    const path = `${PLATFORM_ROUTES.marketingPresaleHighlights}/${encodeURIComponent(id)}`;
    return assertPresaleHighlight(await this.sendJson(path, "PATCH", patch));
  }

  async removePresaleHighlight(id: string): Promise<void> {
    const path = `${PLATFORM_ROUTES.marketingPresaleHighlights}/${encodeURIComponent(id)}`;
    await this.sendJson(path, "DELETE");
  }

  /**
   * Plano Loja/Twotrees, Fase 9 — banners promocionais. Mesmo CRUD de
   * destaques acima; a Loja reflete sem redeploy (GET /api/marketing/banners,
   * rota pública separada).
   */
  async listBanners(): Promise<Banner[]> {
    return (await this.getList(PLATFORM_ROUTES.marketingBanners)).map(assertBanner);
  }

  async addBanner(input: { imageUrl: string; altText: string; linkUrl?: string | null; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }): Promise<Banner> {
    return assertBanner(await this.sendJson(PLATFORM_ROUTES.marketingBanners, "POST", input));
  }

  async updateBanner(id: string, patch: { imageUrl?: string; altText?: string; linkUrl?: string | null; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }): Promise<Banner> {
    const path = `${PLATFORM_ROUTES.marketingBanners}/${encodeURIComponent(id)}`;
    return assertBanner(await this.sendJson(path, "PATCH", patch));
  }

  async removeBanner(id: string): Promise<void> {
    const path = `${PLATFORM_ROUTES.marketingBanners}/${encodeURIComponent(id)}`;
    await this.sendJson(path, "DELETE");
  }

  /**
   * Plano Loja/Twotrees, Fase 10 — cupons de desconto. Sem `removeCoupon`: o
   * contrato não tem DELETE (cupom usado tem pedidos que dependem dele) — só
   * `updateCoupon` para desativar (`active: false`).
   */
  async listCoupons(): Promise<Coupon[]> {
    return (await this.getList(PLATFORM_ROUTES.coupons)).map(assertCoupon);
  }

  async addCoupon(input: {
    code: string;
    discountType: CouponDiscountType;
    discountValue: number;
    active?: boolean;
    startsAt?: string | null;
    endsAt?: string | null;
    minSubtotalCents?: number | null;
    maxUses?: number | null;
    maxUsesPerCustomer?: number | null;
  }): Promise<Coupon> {
    return assertCoupon(await this.sendJson(PLATFORM_ROUTES.coupons, "POST", input));
  }

  async updateCoupon(id: string, patch: {
    discountType?: CouponDiscountType;
    discountValue?: number;
    active?: boolean;
    startsAt?: string | null;
    endsAt?: string | null;
    minSubtotalCents?: number | null;
    maxUses?: number | null;
    maxUsesPerCustomer?: number | null;
  }): Promise<Coupon> {
    const path = `${PLATFORM_ROUTES.coupons}/${encodeURIComponent(id)}`;
    return assertCoupon(await this.sendJson(path, "PATCH", patch));
  }

  /**
   * Dataset das telas de catálogo/estoque/divergências. As duas fontes são
   * buscadas em paralelo e cada uma declara o seu estado; falha real (5xx,
   * payload fora do contrato, 502 do operacional) propaga, porque "não
   * consegui comparar" não pode parecer "nada divergente".
   */
  async loadCatalogDataset(): Promise<CatalogDataset> {
    const [products, snapshot, syncLog] = await Promise.all([
      this.loadPlatformCatalog(),
      this.loadBlingSnapshot(),
      // Mesmo dataset de propósito: o evento de sync que a divergência cita é
      // lido junto da comparação, então a tela de destino mostra o MESMO
      // registro que originou a linha — não uma consulta separada que poderia
      // discordar.
      this.loadBlingSyncLog()
    ]);
    return {
      products: products ?? [],
      snapshot,
      syncLog,
      sources: {
        platformCatalog: products === null ? "awaiting_contract" : "live",
        blingSnapshot: snapshot === null ? "awaiting_contract" : "live",
        blingSyncLog: syncLog === null ? "awaiting_contract" : "live"
      }
    };
  }

  /**
   * Pedidos unificados multicanal (Task 12). `null` = rota não publicada neste
   * ambiente: a tela de pedidos declara a ausência e mostra o recorte do
   * checkout da Loja identificado como tal, em vez de passar a Loja por
   * "todos os canais".
   */
  async loadPlatformOrders(range?: OrdersRange): Promise<PlatformUnifiedOrder[] | null> {
    // O período vai ao servidor (`from`/`to`): o filtro roda no banco, não carregamos 2 mil pedidos para recortar na tela.
    const path = range ? `${PLATFORM_ROUTES.platformOrders}?from=${range.from}&to=${range.to}` : PLATFORM_ROUTES.platformOrders;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload.map(assertPlatformUnifiedOrder);
  }

  /** Um pedido com linha do tempo e o dado completo do canal; `null` = não existe (ou rota ausente). */
  async loadPlatformOrder(id: string): Promise<PlatformUnifiedOrder | null> {
    const path = `${PLATFORM_ROUTES.platformOrders}/${encodeURIComponent(id)}`;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    return assertPlatformUnifiedOrder(await response.json());
  }

  /** Última leitura do Bling para o Supabase; `null` = rota ausente neste ambiente. */
  async loadOrdersSyncState(): Promise<OrdersSyncState | null> {
    const path = `${PLATFORM_ROUTES.platformOrders}/sync`;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501 || response.status === 401 || response.status === 403) return null;
    if (!response.ok) throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    return (await response.json()) as OrdersSyncState;
  }

  /** "Atualizar agora": lê do Bling o que mudou e grava no Supabase. */
  async syncOrdersNow(): Promise<OrdersSyncResult> {
    return (await this.sendJson(`${PLATFORM_ROUTES.platformOrders}/sync`, "POST", {})) as OrdersSyncResult;
  }

  async loadOrdersDataset(range?: OrdersRange): Promise<OrdersDataset> {
    const orders = await this.loadPlatformOrders(range);
    return {
      orders: orders ?? [],
      sources: { platformOrders: orders === null ? "awaiting_contract" : "live" }
    };
  }

  /**
   * Registra uma venda de canal que não é o checkout da Loja (WhatsApp,
   * marketplace, venda manual). A ORIGEM e o identificador externo são parte
   * do corpo porque são requisito do registro — não metadado opcional.
   */
  async createPlatformOrder(input: CreatePlatformOrderInput): Promise<PlatformUnifiedOrder> {
    return assertPlatformUnifiedOrder(await this.sendJson(PLATFORM_ROUTES.platformOrders, "POST", input));
  }

  /** Origens de aquisição cadastradas (ativas e inativas), na ordem de exibição. */
  async listSalesOrigins(): Promise<SalesOriginOption[]> {
    return (await this.getList("/api/platform/acquisition/origins")) as SalesOriginOption[];
  }

  async createSalesOrigin(label: string): Promise<SalesOriginOption> {
    return (await this.sendJson("/api/platform/acquisition/origins", "POST", { label })) as SalesOriginOption;
  }

  /** Só ativa/desativa ou renomeia: origem nunca é apagada, o histórico dos pedidos aponta para o slug. */
  async updateSalesOrigin(slug: string, patch: { active?: boolean; label?: string }): Promise<SalesOriginOption> {
    return (await this.sendJson(`/api/platform/acquisition/origins/${encodeURIComponent(slug)}`, "PATCH", patch)) as SalesOriginOption;
  }

  /** Marca origem/estratégia/responsável de um pedido existente. */
  async setOrderAcquisition(orderId: string, input: OrderAcquisitionInput): Promise<void> {
    await this.sendJson(`${PLATFORM_ROUTES.platformOrders}/${encodeURIComponent(orderId)}/acquisition`, "PATCH", input);
  }

  /** Conversas por origem no período (denominador da conversão). `null` = rota não publicada neste ambiente. */
  async loadAcquisitionContacts(range?: { from: string; to: string }): Promise<Record<string, number> | null> {
    const query = range ? `?from=${range.from}&to=${range.to}` : "";
    try {
      const payload = (await this.getJson(`/api/platform/acquisition/contacts${query}`)) as { contactsByOrigin?: Record<string, number> };
      return payload.contactsByOrigin ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Avança o pedido na máquina de estados. O servidor valida a transição com a
   * MESMA função pura da tela (`checkTransition`) e recusa com 422 o que não é
   * permitido ou o que está sem o dado que a justifica — a mensagem chega
   * legível à tela por `PlatformApiError`.
   *
   * O ator NÃO viaja no corpo: quem registrou a transição é derivado da sessão
   * no servidor, como a autoria do histórico comercial da Task 11.
   */
  async transitionPlatformOrder(
    orderId: string,
    input: { to: PlatformOrderState } & OrderTransitionInput
  ): Promise<PlatformUnifiedOrder> {
    const path = `${PLATFORM_ROUTES.platformOrders}/${encodeURIComponent(orderId)}/transitions`;
    return assertPlatformUnifiedOrder(await this.sendJson(path, "POST", input));
  }

  // -------------------------------------------------------------------------
  // Task 13 — CRM 360. Cada fonte é lida com a MESMA tolerância das demais:
  // 404/501 devolve `null` ("rota não publicada neste ambiente") e o dataset
  // marca aquele bloco como `awaiting_contract`. É o que impede a tela de
  // dizer "cliente sem oportunidade" quando o que houve foi rota ausente.
  // -------------------------------------------------------------------------

  /** Lê uma lista tolerando rota ausente; `null` = contrato não publicado. */
  private async loadOptionalList(path: string): Promise<unknown[] | null> {
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload;
  }

  /** Saldo por filial; 404/501 = rota não publicada (a tela declara a pendência, nunca zero). */
  // ---- Financeiro (contas a pagar/receber, saldo inicial). 404/501 = rota não publicada neste ambiente.
  private async loadFinanceList<T>(path: string, assert: (value: unknown) => T): Promise<T[] | null> {
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    const payload = await response.json();
    if (!Array.isArray(payload)) throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    return payload.map(assert);
  }

  loadPayables(): Promise<Payable[] | null> {
    return this.loadFinanceList(PLATFORM_ROUTES.financePayables, assertPayable);
  }

  async createPayable(input: NewPayableInput): Promise<Payable[]> {
    const created = await this.sendJson(PLATFORM_ROUTES.financePayables, "POST", input);
    return (Array.isArray(created) ? created : []).map(assertPayable);
  }

  async updatePayable(id: string, input: SettleInput | FinanceAction | Record<string, unknown>): Promise<Payable> {
    return assertPayable(await this.sendJson(`${PLATFORM_ROUTES.financePayables}/${encodeURIComponent(id)}`, "PATCH", this.toServerAction(input)));
  }

  loadReceivables(): Promise<Receivable[] | null> {
    return this.loadFinanceList(PLATFORM_ROUTES.financeReceivables, assertReceivable);
  }

  async createReceivable(input: NewReceivableInput): Promise<Receivable[]> {
    const created = await this.sendJson(PLATFORM_ROUTES.financeReceivables, "POST", input);
    return (Array.isArray(created) ? created : []).map(assertReceivable);
  }

  async updateReceivable(id: string, input: SettleInput | FinanceAction | Record<string, unknown>): Promise<Receivable> {
    return assertReceivable(await this.sendJson(`${PLATFORM_ROUTES.financeReceivables}/${encodeURIComponent(id)}`, "PATCH", this.toServerAction(input)));
  }

  async loadFinanceSettings(): Promise<FinanceSettings | null> {
    const path = PLATFORM_ROUTES.financeSettings;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    return assertFinanceSettings(await response.json());
  }

  async saveFinanceSettings(input: FinanceSettings): Promise<FinanceSettings> {
    return assertFinanceSettings(await this.sendJson(PLATFORM_ROUTES.financeSettings, "PUT", input));
  }

  /** `pay`/`receive` levam a data e o valor da baixa com os nomes que a Edge espera. */
  private toServerAction(input: SettleInput | FinanceAction | Record<string, unknown>): Record<string, unknown> {
    if (!("action" in input) || (input.action !== "pay" && input.action !== "receive")) return input as Record<string, unknown>;
    const settle = input as SettleInput;
    const paid = settle.action === "pay";
    return {
      action: settle.action,
      ...(settle.at ? { [paid ? "paidAt" : "receivedAt"]: settle.at } : {}),
      ...(settle.amountCents ? { [paid ? "paidAmountCents" : "receivedAmountCents"]: settle.amountCents } : {}),
      ...(settle.paymentMethod ? { paymentMethod: settle.paymentMethod } : {})
    };
  }

  async loadBranchStock(): Promise<BranchStockSnapshot | null> {
    const path = PLATFORM_ROUTES.branchStock;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    return assertBranchStockSnapshot(await response.json());
  }

  /** C2: a equipe ajusta o saldo de um produto numa filial (reservado não é editável aqui). */
  async saveBranchBalance(input: { branchId: string; productId: string; balance: number }): Promise<void> {
    await this.sendJson(PLATFORM_ROUTES.branchStock, "PUT", input);
  }

  /** C1: aplicações cadastradas; 404/501 = rota não publicada (a tela declara a pendência). */
  async loadApplications(): Promise<ProductApplication[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.applications);
    return payload === null ? null : payload.map(assertProductApplication);
  }

  async addApplication(input: { productId: string; brand: string; model: string; yearFrom?: number; yearTo?: number; note?: string }): Promise<ProductApplication> {
    return assertProductApplication(await this.sendJson(PLATFORM_ROUTES.applications, "POST", input));
  }

  async deleteApplication(id: string): Promise<void> {
    await this.sendJson(`${PLATFORM_ROUTES.applications}/${encodeURIComponent(id)}`, "DELETE");
  }

  /** C3: links de pagamento já gerados para o pedido (mais recente primeiro). */
  async listPaymentLinks(platformOrderId: string): Promise<PaymentLink[]> {
    const response = await this.request(`/api/platform/orders/${encodeURIComponent(platformOrderId)}/payment-links`);
    if (response.status === 404 || response.status === 501) return [];
    if (!response.ok) throw new PlatformApiError("payment-links", response.status, `request_failed:payment-links:${response.status}`);
    const payload = await response.json();
    return Array.isArray(payload) ? payload.map(assertPaymentLink) : [];
  }

  /** C3: gera um link de cobrança para o pedido (só pedido aguardando pagamento). */
  async createPaymentLink(platformOrderId: string): Promise<PaymentLink> {
    return assertPaymentLink(await this.sendJson(`/api/platform/orders/${encodeURIComponent(platformOrderId)}/payment-links`, "POST", {}));
  }

  /** C4: anúncios do Mercado Livre; 404/501 = rota não publicada (a tela declara a pendência). */
  async loadMarketplaceListings(): Promise<MarketplaceListing[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.marketplaceListings);
    return payload === null ? null : payload.map(assertMarketplaceListing);
  }

  async loadCrmCustomers(): Promise<CrmCustomer[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.crmCustomers);
    return payload === null ? null : payload.map(assertCrmCustomer);
  }

  async loadCrmCompanies(): Promise<CrmCompany[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.crmCompanies);
    return payload === null ? null : payload.map(assertCrmCompany);
  }

  async loadCrmOpportunities(): Promise<CrmOpportunity[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.crmOpportunities);
    return payload === null ? null : payload.map(assertCrmOpportunity);
  }

  async loadCrmQuotes(): Promise<CrmQuote[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.crmQuotes);
    return payload === null ? null : payload.map(assertCrmQuote);
  }

  async loadCrmTasks(): Promise<CrmTask[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.crmTasks);
    return payload === null ? null : payload.map(assertCrmTask);
  }

  async loadCrmSegments(): Promise<CrmSegment[] | null> {
    const payload = await this.loadOptionalList(PLATFORM_ROUTES.crmSegments);
    return payload === null ? null : payload.map(assertCrmSegment);
  }

  /**
   * Dataset das telas de CRM. As seis fontes são buscadas em paralelo e cada
   * uma declara o seu estado — a visão 360 precisa poder dizer "o cadastro
   * respondeu, o pipeline não", em vez de falhar inteira ou mentir por omissão.
   */
  async loadCrmDataset(): Promise<CrmDataset> {
    const [customers, companies, opportunities, quotes, tasks, segments] = await Promise.all([
      this.loadCrmCustomers(),
      this.loadCrmCompanies(),
      this.loadCrmOpportunities(),
      this.loadCrmQuotes(),
      this.loadCrmTasks(),
      this.loadCrmSegments()
    ]);
    return {
      customers: customers ?? [],
      companies: companies ?? [],
      opportunities: opportunities ?? [],
      quotes: quotes ?? [],
      tasks: tasks ?? [],
      segments: segments ?? [],
      sources: {
        customers: customers === null ? "awaiting_contract" : "live",
        companies: companies === null ? "awaiting_contract" : "live",
        opportunities: opportunities === null ? "awaiting_contract" : "live",
        quotes: quotes === null ? "awaiting_contract" : "live",
        tasks: tasks === null ? "awaiting_contract" : "live",
        segments: segments === null ? "awaiting_contract" : "live"
      }
    };
  }

  /**
   * Visão 360 do cliente pelo runtime persistente do CRM.
   *
   * `selector` é o identificador que a tela tem em mãos: o id do cadastro
   * canônico, ou o telefone/e-mail do canal quando a tela veio de uma conversa.
   * Quem resolve identidade é o servidor (`resolve_customer_by_identity`,
   * 0020) — a tela não compara telefone para decidir de quem é o histórico.
   *
   * `null` em três casos, todos "não consegui verificar" e nenhum "cliente sem
   * registro": rota não publicada neste ambiente (404/501), identidade sem
   * vínculo comprovado (`customer_identity_unresolved`) e credencial do runtime
   * ausente (503 `crm_runtime_credential_pending`). A tela cai nas listas do
   * CRM e DECLARA que a agregação canônica não respondeu.
   */
  async loadCrm360(selector: { customerId?: string; phone?: string; email?: string }): Promise<Crm360Payload | null> {
    const query = selector.customerId
      ? `customer=${encodeURIComponent(selector.customerId)}`
      : selector.phone
        ? `phone=${encodeURIComponent(selector.phone)}`
        : selector.email
          ? `email=${encodeURIComponent(selector.email)}`
          : "";
    if (!query) return null;
    const path = `${PLATFORM_ROUTES.crm360}?${query}`;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501 || response.status === 503) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    return assertCrm360Payload(await response.json());
  }

  /** Abre uma oportunidade para um cliente canônico já existente. */
  async createCrmOpportunity(input: CreateCrmOpportunityInput): Promise<CrmOpportunity> {
    return assertCrmOpportunity(await this.sendJson(PLATFORM_ROUTES.crmOpportunities, "POST", input));
  }

  /**
   * Muda o estágio da oportunidade. O servidor valida com a MESMA função pura
   * da tela (`checkStageTransition`) e recusa com 422 o que não é permitido ou
   * o que está sem a prova do fechamento (motivo da perda, pedido da venda).
   *
   * O ator NÃO viaja no corpo: quem mudou o estágio é derivado da sessão no
   * servidor, como a autoria do histórico comercial (Task 11) e das transições
   * de pedido (Task 12).
   */
  async advanceOpportunityStage(
    opportunityId: string,
    input: { to: LeadStage } & StageTransitionInput
  ): Promise<CrmOpportunity> {
    const path = `${PLATFORM_ROUTES.crmOpportunities}/${encodeURIComponent(opportunityId)}/stage`;
    return assertCrmOpportunity(await this.sendJson(path, "POST", input));
  }

  /** Registra um follow-up/tarefa comercial (prazo e dono obrigatórios). */
  async createCrmTask(input: CreateCrmTaskInput): Promise<CrmTask> {
    return assertCrmTask(await this.sendJson(PLATFORM_ROUTES.crmTasks, "POST", input));
  }

  /**
   * Conclui (ou cancela) uma tarefa com o resultado. O resultado é obrigatório
   * no servidor: follow-up fechado sem dizer o que aconteceu apaga a única
   * informação que a próxima pessoa precisava.
   */
  async closeCrmTask(taskId: string, input: { status: "done" | "cancelled"; outcome: string }): Promise<CrmTask> {
    const path = `${PLATFORM_ROUTES.crmTasks}/${encodeURIComponent(taskId)}/close`;
    return assertCrmTask(await this.sendJson(path, "POST", input));
  }

  /** Emite um orçamento (proposta) com o preço travado na emissão. */
  async createCrmQuote(input: CreateCrmQuoteInput): Promise<CrmQuote> {
    return assertCrmQuote(await this.sendJson(PLATFORM_ROUTES.crmQuotes, "POST", input));
  }

  async getPricingSummary(): Promise<PricingSummaryResponse> {
    return assertPricingSummaryResponse(await this.getJson(PLATFORM_ROUTES.pricingSummary));
  }

  async listNotifications(): Promise<PlatformNotification[]> {
    return (await this.getList(PLATFORM_ROUTES.notifications)).map(assertPlatformNotification);
  }

  /**
   * Trilha de auditoria do event store. A rota responde 501 quando o store
   * configurado não sabe listar tudo (`eventStore.all` ausente); nesse caso
   * devolvemos `null` = "fonte não publicada", e a timeline do Customer 360
   * declara a ausência em vez de parecer "nenhuma ação registrada".
   */
  async listAuditEvents(): Promise<PlatformAuditEvent[] | null> {
    const path = PLATFORM_ROUTES.audit;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) return null;
    return payload.map(assertPlatformAuditEvent);
  }

  /**
   * Configuração das integrações (Entrega 0, R3–R5). O servidor monta o
   * registry a partir do ambiente e devolve SÓ metadados — estado, flag e os
   * nomes de secret com um booleano configurado; o browser nunca lê env de
   * segredo. `null` = rota não publicada neste ambiente (404/501); a tela
   * `/configuracoes` declara a ausência em vez de dizer "nada configurado".
   */
  /** Sessão + permissão resolvida do usuário logado; `null` = rota não publicada (menu completo). */
  async loadMyAccess(): Promise<StaffUser | null> {
    const path = `${PLATFORM_ROUTES.users}/me`;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      // 403 carrega o motivo (conta bloqueada x sem papel de staff): a tela troca por "Conta bloqueada".
      const detail = response.status === 403 ? await response.json().catch(() => null) : null;
      const reason = detail && typeof detail === "object" && "error" in detail ? String((detail as { error: unknown }).error) : "request_failed";
      throw new PlatformApiError(path, response.status, `${reason}:${path}:${response.status}`);
    }
    return assertStaffUser(await response.json());
  }

  async listStaffUsers(): Promise<StaffUser[]> {
    return (await this.getList(PLATFORM_ROUTES.users)).map(assertStaffUser);
  }

  async createStaffUser(input: NewStaffUserInput): Promise<StaffUser> {
    return assertStaffUser(await this.sendJson(PLATFORM_ROUTES.users, "POST", input));
  }

  async updateStaffUser(id: string, patch: StaffUserPatch): Promise<StaffUser> {
    return assertStaffUser(await this.sendJson(`${PLATFORM_ROUTES.users}/${encodeURIComponent(id)}`, "PATCH", patch));
  }

  async deleteStaffUser(id: string): Promise<void> {
    await this.sendJson(`${PLATFORM_ROUTES.users}/${encodeURIComponent(id)}`, "DELETE");
  }

  async listStaffUserEvents(id: string): Promise<StaffUserEvent[]> {
    return (await this.getList(`${PLATFORM_ROUTES.users}/${encodeURIComponent(id)}/events`)).map(assertStaffUserEvent);
  }

  async loadIntegrations(): Promise<PlatformIntegrationStatus[] | null> {
    const path = PLATFORM_ROUTES.configIntegrations;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload.map(assertPlatformIntegrationStatus);
  }

  async getMetrics(): Promise<PlatformMetrics> {
    const payload = await this.getJson(PLATFORM_ROUTES.metrics);
    if (typeof payload !== "object" || payload === null) {
      throw new PlatformApiError(PLATFORM_ROUTES.metrics, 200, "invalid_metrics_payload");
    }
    return payload as PlatformMetrics;
  }

  /**
   * Casos de garantia/RMA. A rota é publicada pela Task 15 (migration 0034);
   * enquanto o servidor responder 404/501 devolvemos `null` = "fonte ainda não
   * existe". Quando a Task 15 subir a rota, o KPI passa a `live` sozinho, sem
   * mudar o dashboard.
   */
  async listRmaCases(): Promise<PlatformRmaCase[] | null> {
    const path = PLATFORM_ROUTES.rmaCases;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) return null;
    return payload.map(assertPlatformRmaCase);
  }

  /**
   * Dataset de garantia/RMA (Task 15). Mesma tolerância das demais fontes:
   * 404/501 devolve `awaiting_contract` e a tela declara a ausência em vez de
   * dizer "nenhum caso".
   */
  async loadRmaDataset(): Promise<RmaDataset> {
    const cases = await this.listRmaCases();
    return {
      cases: cases ?? [],
      sources: { rmaCases: cases === null ? "awaiting_contract" : "live" }
    };
  }

  /**
   * Abre um caso de garantia a partir de um pedido unificado. O servidor
   * localiza o cliente e o produto do pedido e nasce o caso em `defect`. O
   * ator vem da sessão no servidor, não do corpo.
   */
  async openRmaCase(input: { platformOrderId: string; productId?: string | null; defectSummary: string }): Promise<PlatformRmaCase> {
    return assertPlatformRmaCase(await this.sendJson(PLATFORM_ROUTES.rmaCases, "POST", input));
  }

  /**
   * Avança a etapa do caso de garantia. O servidor valida com a MESMA função
   * pura da tela (`checkRmaTransition`) e recusa com 422 o que não é permitido
   * ou o que está sem a prova que a justifica (nº de série no protocolo,
   * comprovante no diagnóstico, resultado na resolução). Reembolso/troca gera
   * referência para o Financeiro — não executa pagamento. O ator vem da sessão.
   */
  async advanceRmaState(caseId: string, input: { to: RmaState } & RmaTransitionInput): Promise<PlatformRmaCase> {
    const path = `${PLATFORM_ROUTES.rmaCases}/${encodeURIComponent(caseId)}/transitions`;
    return assertPlatformRmaCase(await this.sendJson(path, "POST", input));
  }

  // -------------------------------------------------------------------------
  // Task 16 — financeiro gerencial. GERENCIAL, nunca fiscal; só LEITURA (o
  // cliente não executa pagamento nem estorno). Mesma tolerância das demais
  // fontes: 404/501 devolve `null` ("rota não publicada neste ambiente") e a
  // tela declara a ausência em vez de dizer "tudo conciliado"/"margem zero".
  // -------------------------------------------------------------------------

  /**
   * Resumo financeiro gerencial (conciliação + margens + lançamentos). `null`
   * = rota não publicada neste ambiente. O `source` do resumo diz se os
   * números vêm de credencial real ou do simulador (taxa do adquirente) — a
   * tela mostra isso, nunca passa simulador por real.
   */
  async loadFinancialSummary(): Promise<FinancialSummaryResponse | null> {
    const path = PLATFORM_ROUTES.financialSummary;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    return assertFinancialSummaryResponse(await response.json());
  }

  async loadFinancialDataset(): Promise<FinancialDataset> {
    const summary = await this.loadFinancialSummary();
    if (!summary) {
      return emptyFinancialDataset();
    }
    return {
      summary: summary.summary,
      reconciliations: summary.reconciliations,
      orderMargins: summary.orderMargins,
      productMargins: summary.productMargins,
      entries: summary.entries,
      accounts: summary.accounts,
      sources: { financial: "live" }
    };
  }

  // -------------------------------------------------------------------------
  // Task 14 — expedição. Mesma tolerância das demais fontes: 404/501 devolve
  // `null` ("rota não publicada neste ambiente") e a tela declara a ausência
  // em vez de dizer "nenhuma expedição".
  // -------------------------------------------------------------------------

  async loadFulfillmentTasks(): Promise<FulfillmentTask[] | null> {
    const path = PLATFORM_ROUTES.fulfillment;
    const response = await this.request(path);
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) {
      throw new PlatformApiError(path, response.status, `request_failed:${path}:${response.status}`);
    }
    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new PlatformApiError(path, 200, `expected_array_response:${path}`);
    }
    return payload.map(assertFulfillmentTask);
  }

  async loadFulfillmentDataset(): Promise<FulfillmentDataset> {
    const tasks = await this.loadFulfillmentTasks();
    return {
      tasks: tasks ?? [],
      sources: { fulfillmentTasks: tasks === null ? "awaiting_contract" : "live" }
    };
  }

  /**
   * Abre a ordem de expedição de um pedido pago. O servidor recusa pedido que
   * não existe ou que ainda não está pago (a expedição não começa antes do
   * pagamento) e é idempotente: pedido já em expedição devolve a tarefa atual.
   */
  async openFulfillmentTask(platformOrderId: string): Promise<FulfillmentTask> {
    return assertFulfillmentTask(await this.sendJson(PLATFORM_ROUTES.fulfillment, "POST", { platformOrderId }));
  }

  /**
   * Avança a etapa da expedição. O servidor valida com a MESMA função pura da
   * tela (`checkStageTransition`) e recusa com 422 o que não é permitido ou o
   * que está sem o dado que a justifica (transportadora/rastreio no despacho,
   * motivo na reversa). O ator vem da sessão no servidor, não do corpo.
   */
  async advanceFulfillmentStage(
    taskId: string,
    input: { to: FulfillmentStage } & FulfillmentStageInput
  ): Promise<FulfillmentTask> {
    const path = `${PLATFORM_ROUTES.fulfillment}/${encodeURIComponent(taskId)}/transitions`;
    return assertFulfillmentTask(await this.sendJson(path, "POST", input));
  }

  /**
   * Registra uma ocorrência (atraso/avaria/endereço/ausente/extravio) na ordem
   * de expedição. O servidor exige tipo conhecido e nota não vazia: ocorrência
   * sem relato é um alarme sem informação, recusado em vez de gravado vazio.
   */
  async registerFulfillmentOccurrence(
    taskId: string,
    input: { occurrenceKind: OccurrenceKind; occurrenceNote: string }
  ): Promise<FulfillmentTask> {
    const path = `${PLATFORM_ROUTES.fulfillment}/${encodeURIComponent(taskId)}/occurrences`;
    return assertFulfillmentTask(await this.sendJson(path, "POST", input));
  }

  /**
   * Carrega o dataset do dashboard em paralelo.
   *
   * Dois comportamentos distintos, de propósito:
   * - **Falha real** (5xx, payload fora do contrato, rota quebrada) propaga: a
   *   tela mostra o erro em vez de um dashboard parcial que o operador leria
   *   como verdade.
   * - **Rota não publicada neste ambiente** (404/501 em conversas/pricing, que
   *   o Worker de produção ainda não serve) entra como fonte
   *   `awaiting_contract`: o dashboard monta, os indicadores que dependem dela
   *   declaram a ausência (nunca zero) e os outros sete seguem reais.
   */
  async loadDashboardDataset(): Promise<DashboardDataset> {
    const [orders, conversations, catalog, pricing, notifications, rmaCases, platformCatalog] = await Promise.all([
      this.listOrders(),
      this.loadConversations(),
      this.listCatalogEntries(),
      this.loadPricingSummary(),
      this.listNotifications(),
      this.listRmaCases(),
      // Task 11: o catálogo em duas camadas entra no dataset do dashboard por
      // causa das RESERVAS. Sem elas o KPI de estoque crítico mediria estoque
      // bruto e discordaria da tela de disponibilidade — duas verdades para o
      // mesmo número. Rota ausente mantém o comportamento anterior (sem
      // subtração) e a nota do KPI declara isso.
      this.loadPlatformCatalog()
    ]);
    return {
      orders,
      conversations: conversations ?? [],
      catalog,
      pricing: pricing ?? emptyPricingSummary(),
      notifications,
      rmaCases,
      platformCatalog,
      sources: {
        conversations: conversations === null ? "awaiting_contract" : "live",
        pricing: pricing === null ? "awaiting_contract" : "live",
        platformCatalog: platformCatalog === null ? "awaiting_contract" : "live"
      }
    };
  }
}
